import { FC, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'
import { TransProps } from 'next-translate'
import { FlexboxGrid } from 'rsuite'
import { useFeatureValue } from '@growthbook/growthbook-react'

import useCategoryDataStructure from '../../hooks/useCategoryDataStructure'
import { NoProductResults } from '../../Graphics/EmptyStateGraphics'
import { getRandomItemFromlist } from '../../../utils/util'
import { getSupplierName } from '../../../lib/supplier'
import { Supplier } from '../../../utils/types/Product'
import MainCategoriesCarousel from '../../LandingPage/MainCategoriesCarousel/MainCategoriesCarousel'
import SuppliersCarousel from '../../LandingPage/SuppliersCarousel/SuppliersCarousel'
import UserEmailForm from './UserEmailForm'
import RfqButton from './RfqButton'
import { MODAL_HASHES } from '../../../utils/constants'
import SafeTranslate from '../../common/SafeTranslate'

import styles from '../../../styles/EmptyState.module.less'

const EmptyStateSubTitle: FC<TransProps> = ({ i18nKey, ...props }) => {
  const { asPath } = useRouter()
  const cleanAsPath = asPath.split('#')[0] // Avoid double hashes when accessed directly with #hash

  const rfqLink = `${cleanAsPath}${MODAL_HASHES.RFQ_URL_HASH}`

  return (
    <SafeTranslate
      {...props}
      i18nKey={`common:${i18nKey}`}
      components={{ Link: <Link
        shallow
        href={rfqLink}
      /> }}
    />
  )
}

interface EmptyProductsViewProps {
  supplier?: Supplier
}

const EmptyProductsView: FC<EmptyProductsViewProps> = ({ supplier }) => {
  const [title, setTitle] = useState('')
  const { t } = useTranslation('products')
  const { query } = useRouter()
  const emptyStateFeature = useFeatureValue('empty-state-track-order', 'original')

  const { selectedCategory, parentNodes } = useCategoryDataStructure()

  const allTitles = [
    'Matching products found',
    'Matching products are in stock',
  ].map((text) => t(`common:${text}`))

  // Setting a random title and
  // making sure the title doesn't change on every rerender
  useEffect(() => {
    setTitle(getRandomItemFromlist(allTitles))
  }, [])

  const totalFilters = Object.keys(query).filter((param) => param.startsWith('f_')).length

  let subTitleKey = ''
  let subTitleParams = {}

  if (emptyStateFeature === 'only-email-field') {
    subTitleKey = 'empty_state_only_email'
    subTitleParams = { count: totalFilters }
  } else if (query.search) {
    subTitleKey = 'empty_state_found_product_for_searchterm'
    subTitleParams = { search_term: query.search }
  } else if (totalFilters > 0) {
    subTitleKey = 'empty_state_found_products_for_filter'

    subTitleParams = { count: totalFilters }
  } else if (selectedCategory) {
    subTitleKey = 'empty_state_found_products_for_category'

    const selectedCategoryLabel = Object.values(parentNodes)
      .find((node) => node.code === selectedCategory)
      ?.label

    subTitleParams = { category: selectedCategoryLabel }
  } else if (supplier) {
    subTitleKey = 'empty_state_found_product_for_supplier'

    subTitleParams = { supplier_name: getSupplierName(supplier) }
  }

  const renderEmptyStateWithFeatureFlag = () => {
    if (emptyStateFeature === 'both-fields') {
      return (
        <>
          <RfqButton />
          <FlexboxGrid className="margin-top-spacer padding-top-spacer-half">
            <FlexboxGrid.Item className={styles.line} />
            <FlexboxGrid.Item className="margin-right-spacer-tripple margin-left-spacer-tripple">{t('modals:or')}</FlexboxGrid.Item>
            <FlexboxGrid.Item className={styles.line} />
          </FlexboxGrid>
          <p className="margin-top-spacer padding-top-spacer-half">
            {t('Ask us to find the product for you')}
          </p>
          <UserEmailForm />
        </>
      )
    }

    if (emptyStateFeature === 'only-email-field') {
      return (
        <UserEmailForm />
      )
    }

    return <RfqButton />
  }

  return (
    <>
      <div className={`${styles['empty-state']}`}>
        <div className={styles['empty-state-graphic-container']}>
          <NoProductResults />
        </div>
        <h4 className="margin-bottom-spacer">
          {t(title)}
        </h4>
        <p className={styles['empty-state-subtitle']}>
          <EmptyStateSubTitle
            values={subTitleParams}
            i18nKey={subTitleKey}
          />
        </p>
        {renderEmptyStateWithFeatureFlag()}
      </div>
      <div className={styles['carousel-stack']}>
        <MainCategoriesCarousel
          className={styles['category-carousel']}
          label={t('landing:Simplified Purchasing Starts Here')}
        />
        <SuppliersCarousel />
      </div>
    </>
  )
}
export default EmptyProductsView
